import React, { useState } from "react";
import { ChevronDown, X, Filter, Globe, Download } from "lucide-react";
import { DateRangeFilter } from "../customers/customerDetail/DateRangeFilter";
import { useCountries } from "../../hooks/useCountries";
import { ReactComponent as VerifyIcon } from "../../assets/ic_check_verified.svg";
import PrimaryButton from "../ui/PrimaryButton";

interface DailyEntryFiltersProps {
  selectedDate?: string;
  selectedDateFrom?: string;
  selectedDateTo?: string;
  selectedCustomerId?: string;
  selectedCountryId?: string;
  isCreatingCustomerEntry?: boolean;
  onDateChange?: (date: string) => void;
  onDateRangeChange?: (dateFrom: string, dateTo: string) => void;
  onDateClear?: () => void;
  onCountryChange?: (countryId: string) => void;
  onVerifyCustomer?: () => void;
  onClearAll?: () => void;
  onExportExcel?: () => void;
}

export const DailyEntryFilters: React.FC<DailyEntryFiltersProps> = ({
  selectedDate,
  selectedDateFrom,
  selectedDateTo,
  selectedCustomerId,
  selectedCountryId,
  isCreatingCustomerEntry,
  onDateChange,
  onDateRangeChange,
  onDateClear,
  onCountryChange,
  onClearAll,
  onVerifyCustomer,
  onExportExcel,
}) => {
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  const { data: countries, isLoading: isCountriesLoading } = useCountries({
    skip: 0,
    limit: 1000,
  });

  const selectedCountry = countries?.countries.find(
    (c) => c.id === selectedCountryId,
  );

  const hasActiveFilters = Boolean(
    selectedDate ||
      (selectedDateFrom && selectedDateTo) ||
      selectedCustomerId ||
      selectedCountryId,
  );

  const getFilterSummary = () => {
    const filters = [];

    if (selectedDate) {
      filters.push(`Date: ${new Date(selectedDate).toLocaleDateString()}`);
    } else if (selectedDateFrom && selectedDateTo) {
      filters.push(
        `Date: ${new Date(selectedDateFrom).toLocaleDateString()} - ${new Date(selectedDateTo).toLocaleDateString()}`,
      );
    }

    if (selectedCountry) {
      filters.push(`Country: ${selectedCountry.name}`);
    }

    return filters.length > 0 ? filters.join(" • ") : "No filters applied";
  };

  return (
    <div className="bg-slate-800 rounded-lg p-4 mb-6">
      <div className="flex flex-col lg:flex-row gap-4 items-center">
        {/* Date Range Filter */}
        <div className="flex-1">
          <DateRangeFilter
            selectedDate={selectedDate}
            selectedDateFrom={selectedDateFrom}
            selectedDateTo={selectedDateTo}
            onDateChange={onDateChange}
            onDateRangeChange={onDateRangeChange}
            onClear={onDateClear}
          />
        </div>

        {/* Country Filter */}
        <div className="relative flex-shrink-0 w-48">
          <button
            onClick={() => {
              setShowCountryDropdown(!showCountryDropdown);
              setShowCustomerDropdown(false);
            }}
            className="flex items-center bg-slate-700 rounded px-4 py-2 text-white border border-slate-600 hover:bg-slate-600 transition-colors w-full"
          >
            <Globe className="w-4 h-4 mr-2 text-slate-400" />
            <span className="truncate flex-1 text-left">
              {selectedCountry ? selectedCountry.name : "All Countries"}
            </span>
            {selectedCountryId && (
              <X
                className="w-4 h-4 ml-2 text-slate-400 hover:text-white flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onCountryChange?.("");
                }}
              />
            )}
            <ChevronDown className="w-4 h-4 ml-2 text-slate-400 flex-shrink-0" />
          </button>

          {showCountryDropdown && (
            <div className="absolute top-full left-0 mt-1 bg-slate-700 rounded-lg shadow-lg border border-slate-600 z-50 w-full max-h-64 overflow-y-auto">
              {isCountriesLoading ? (
                <div className="p-3 text-slate-400 text-center">Loading...</div>
              ) : countries?.countries.length === 0 ? (
                <div className="p-3 text-slate-400 text-center">
                  No countries found
                </div>
              ) : (
                countries?.countries.map((country) => (
                  <button
                    key={country.id}
                    onClick={() => {
                      onCountryChange?.(country.id);
                      setShowCountryDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-slate-600 text-white text-sm border-b border-slate-600 last:border-b-0"
                  >
                    {country.name}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Verify Customer */}
        <div className="flex-shrink-0">
          <PrimaryButton
            onClick={onVerifyCustomer}
            disabled={isCreatingCustomerEntry}
            className="flex w-auto items-center gap-2 px-4 py-2 bg-slate-700 hover"
          >
            <VerifyIcon className="h-4 w-4" />
            {isCreatingCustomerEntry ? "Verifying..." : "Verify Customer"}
          </PrimaryButton>
        </div>

        {/* Export to Excel Button */}
        <div className="flex-shrink-0">
          <button
            onClick={onExportExcel}
            className="flex items-center gap-2 px-4 py-2 h-10 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            title="Export to Excel"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>

        {/* Clear All Filters */}
        {hasActiveFilters && (
          <button
            onClick={onClearAll}
            className="flex items-center gap-2 px-4 py-2 text-slate-400 hover:text-white transition-colors whitespace-nowrap"
          >
            <Filter className="w-4 h-4" />
            Clear All
          </button>
        )}
      </div>

      {/* Filter Summary */}
      {hasActiveFilters && (
        <div className="mt-3 pt-3 border-t border-slate-600">
          <div className="text-sm text-slate-400">
            <span className="font-medium">Active Filters:</span>{" "}
            {getFilterSummary()}
          </div>
        </div>
      )}

      {/* Backdrop */}
      {(showCustomerDropdown || showCountryDropdown) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowCustomerDropdown(false);
            setShowCountryDropdown(false);
          }}
        />
      )}
    </div>
  );
};
